import Layout from '@/layout'
import ParentView from '@/components/ParentView'
import InnerLink from '@/layout/components/InnerLink'

/**
 * 动态路由工具类
 */
class DynamicRouteUtils {
  /**
   * 转换后端路由数据为前端路由格式
   * @param {Array} routes - 后端路由数据
   * @param {boolean} isRoot - 是否为根路由
   * @returns {Array} 前端路由格式
   */
  static transformRoutes(routes, isRoot = true) {
    if (!routes || !Array.isArray(routes)) {
      return []
    }

    return routes.map(route => {
      const routeConfig = {
        path: route.path || '/',
        name: route.name || '',
        component: this.loadComponent(route.funcPage, isRoot),
        hidden: route.hidden == 1,
        alwaysShow: route.alwaysShow == 1,
        visible: route.visible == 1,
        meta: this.buildRouteMeta(route)
      }

      // 处理重定向
      if (route.redirect) {
        routeConfig.redirect = route.redirect
      }

      // 处理权限标识
      if (route.funcPerms) {
        routeConfig.funcPerms = route.funcPerms
      }

      // 递归处理子路由
      if (route.children && route.children.length > 0) {
        routeConfig.children = this.transformRoutes(route.children, false)
      }

      return routeConfig
    })
  }

  /**
   * 动态加载组件
   * @param {string} componentPath - 组件路径
   * @param {boolean} isRoot - 是否为根路由
   * @returns {Function|Object} 组件
   */
  static loadComponent(componentPath, isRoot = false) {
    if (!componentPath) {
      return isRoot ? Layout : () => import('@/views/error-page/404')
    }

    // 处理特殊组件
    switch (componentPath) {
      case 'Layout':
        return Layout
      case 'ParentView':
        return ParentView
      case 'InnerLink':
        return InnerLink
      default:
        // 动态导入组件
        return this.dynamicImport(componentPath)
    }
  }

  /**
   * 动态导入组件
   * @param {string} componentPath - 组件路径
   * @returns {Function} 动态导入函数
   */
  static dynamicImport(componentPath) {
    // 处理路径格式
    let path = componentPath
    
    // 如果路径以@开头，替换为src
    if (path.startsWith('@/')) {
      path = path.replace('@/', '')
    }
    
    // 如果路径不以views开头，添加views前缀
    if (!path.startsWith('views/')) {
      path = `views/${path}`
    }
    
    // 如果路径不以.vue结尾，添加.vue后缀
    if (!path.endsWith('.vue')) {
      path = `${path}.vue`
    }

    // 返回动态导入函数
    return () => {
      console.log(`Loading component: ${path}`)
      return import(`@/${path}`).catch(error => {
        console.error(`Failed to load component: ${componentPath}`, error)
        console.log(`Fallback to 404 page for: ${componentPath}`)
        // 返回404页面作为fallback
        return import('@/views/error-page/404')
      })
    }
  }

  /**
   * 构建路由元数据
   * @param {Object} route - 路由数据
   * @returns {Object} 路由元数据
   */
  static buildRouteMeta(route) {
    const meta = {
      title: route.funcName || route.meta?.title || '未命名页面',
      icon: route.meta?.icon || 'menu',
      noCache: route.meta?.noCache == 1,
      affix: route.meta?.affix == 1,
      breadcrumb: route.meta?.breadcrumb != 0
    }

    // 添加权限信息
    if (route.funcPerms) {
      meta.roles = [route.funcPerms]
    }

    // 添加高亮菜单
    if (route.meta?.activeMenu) {
      meta.activeMenu = route.meta.activeMenu
    }

    // 添加扩展属性
    if (route.meta?.extra) {
      Object.assign(meta, route.meta.extra)
    }

    return meta
  }

  /**
   * 过滤有效路由
   * @param {Array} routes - 路由列表
   * @returns {Array} 过滤后的路由列表
   */
  static filterValidRoutes(routes) {
    if (!routes || !Array.isArray(routes)) {
      return []
    }

    return routes.filter(route => {
      // 过滤掉状态为禁用的路由
      if (route.status != 1) {
        return false
      }

      // 过滤掉隐藏且没有子路由的路由
      // if (route.visible == 0 && (!route.children || route.children.length === 0)) {
      //   return false
      // }
      // if (!route.children || route.children.length === 0) {
      //   return false
      // }

      // 递归过滤子路由
      if (route.children && route.children.length > 0) {
        route.children = this.filterValidRoutes(route.children)
      }

      return true
    })
  }

  /**
   * 扁平化路由树
   * @param {Array} routes - 路由树
   * @returns {Array} 扁平化的路由列表
   */
  static flattenRoutes(routes) {
    const result = []
    
    function flatten(routeList, parentPath = '') {
      routeList.forEach(route => {
        const fullPath = parentPath + route.path
        result.push({
          ...route,
          fullPath
        })
        
        if (route.children && route.children.length > 0) {
          flatten(route.children, fullPath + '/')
        }
      })
    }
    
    flatten(routes)
    return result
  }

  /**
   * 根据权限过滤路由
   * @param {Array} routes - 路由列表
   * @param {Array} permissions - 用户权限列表
   * @returns {Array} 过滤后的路由列表
   */
  static filterRoutesByPermissions(routes, permissions) {
    if (!routes || !Array.isArray(routes)) {
      return []
    }

    if (!permissions || !Array.isArray(permissions)) {
      return routes
    }

    return routes.filter(route => {
      // 如果路由没有权限要求，直接通过
      if (!route.funcPerms) {
        return true
      }

      // 检查用户是否有该路由的权限
      const hasPermission = permissions.includes(route.funcPerms)
      
      if (hasPermission && route.children && route.children.length > 0) {
        // 递归过滤子路由
        route.children = this.filterRoutesByPermissions(route.children, permissions)
      }

      return hasPermission
    })
  }

  /**
   * 生成面包屑导航
   * @param {Array} routes - 路由列表
   * @param {string} currentPath - 当前路径
   * @returns {Array} 面包屑列表
   */
  static generateBreadcrumb(routes, currentPath) {
    const breadcrumb = []
    
    function findPath(routeList, path, parents = []) {
      for (const route of routeList) {
        const currentParents = [...parents, route]
        
        if (route.path === path) {
          breadcrumb.push(...currentParents.filter(r => r.meta?.breadcrumb !== false))
          return true
        }
        
        if (route.children && route.children.length > 0) {
          if (findPath(route.children, path, currentParents)) {
            return true
          }
        }
      }
      return false
    }
    
    findPath(routes, currentPath)
    return breadcrumb
  }

  /**
   * 验证路由配置
   * @param {Object} route - 路由配置
   * @returns {boolean} 是否有效
   */
  static validateRoute(route) {
    if (!route || typeof route !== 'object') {
      return false
    }

    // 必须有路径
    if (!route.path) {
      console.warn('Route missing path:', route)
      return false
    }

    // 必须有组件或重定向
    if (!route.component && !route.redirect) {
      console.warn('Route missing component and redirect:', route)
      return false
    }

    return true
  }
}

export default DynamicRouteUtils